import { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  List,
  ListItemButton,
  ListSubheader,
  Typography,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import UserAvatar from './UserAvatar';

const CC_GROUP = 'Callcenter';
const CENTRAL_GROUP = 'Zentrale / Management';

// Assign each colleague to exactly one group, prioritising Callcenter, then their
// Standort (branch), then central office / management (no branch assigned).
const groupEmployees = (employees, branches, myBranchIds) => {
  const branchName = new Map((branches || []).map(b => [b.id, b.name || 'Standort']));
  const myBranchSet = new Set(myBranchIds || []);
  const groups = new Map();
  const push = (label, emp) => {
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(emp);
  };

  for (const emp of employees) {
    if (emp.isCcEmployee) {
      push(CC_GROUP, emp);
    } else if (emp.branches && emp.branches.length > 0) {
      const primary =
        emp.branches.find(b => myBranchSet.has(b.id)) || emp.branches[0];
      push(branchName.get(primary.id) || primary.name || 'Standort', emp);
    } else {
      push(CENTRAL_GROUP, emp);
    }
  }

  // Order: my branches first, then other branches (alphabetical), Callcenter, Zentrale.
  const myBranchNames = new Set(
    (branches || []).filter(b => myBranchSet.has(b.id)).map(b => b.name)
  );
  const labels = Array.from(groups.keys());
  labels.sort((a, b) => {
    const rank = label => {
      if (myBranchNames.has(label)) return 0;
      if (label === CC_GROUP) return 2;
      if (label === CENTRAL_GROUP) return 3;
      return 1;
    };
    const ra = rank(a);
    const rb = rank(b);
    if (ra !== rb) return ra - rb;
    return a.localeCompare(b, 'de');
  });

  return labels.map(label => ({
    label,
    employees: groups
      .get(label)
      .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || '', 'de')),
  }));
};

const subtitleOf = emp => {
  if (emp.isCcEmployee) return 'Callcenter';
  if (emp.roles && emp.roles.length > 0) return emp.roles.map(r => r.readableName).join(', ');
  if (emp.department) return emp.department;
  return '';
};

export default function EmployeeDirectory({ getDirectory, onlineUsers, onStartChat, startingId }) {
  const [search, setSearch] = useState('');
  const [data, setData] = useState({ employees: [], branches: [], myBranchIds: [] });
  const [loading, setLoading] = useState(true);
  const debounceRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getDirectory(search.trim());
        if (!cancelled && res) setData(res);
      } catch (_e) {
        // errors handled in hook
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(load, search ? 300 : 0);
    return () => {
      cancelled = true;
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, getDirectory]);

  const grouped = useMemo(
    () => groupEmployees(data.employees, data.branches, data.myBranchIds),
    [data]
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      <Box sx={{ p: 1.5 }}>
        <TextField
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder='Mitarbeiter suchen (Name, Nummer …)'
          size='small'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : grouped.length === 0 ? (
          <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', mt: 3, px: 2 }}>
            Keine Mitarbeiter gefunden.
          </Typography>
        ) : (
          <List disablePadding subheader={<li />}>
            {grouped.map(group => (
              <li key={group.label}>
                <ul style={{ padding: 0 }}>
                  <ListSubheader sx={{ bgcolor: 'background.paper', fontWeight: 700 }}>
                    {group.label} ({group.employees.length})
                  </ListSubheader>
                  {group.employees.map(emp => {
                    const online = (onlineUsers && onlineUsers.has(emp.id)) || emp.isOnline;
                    return (
                      <ListItemButton
                        key={emp.id}
                        onClick={() => onStartChat(emp)}
                        disabled={startingId === emp.id}
                        sx={{ gap: 1.5, py: 0.75 }}
                      >
                        <UserAvatar name={emp.displayName} size={40} online={!!online} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant='subtitle2' noWrap>
                            {emp.displayName}
                          </Typography>
                          {subtitleOf(emp) && (
                            <Typography variant='caption' color='text.secondary' noWrap display='block'>
                              {subtitleOf(emp)}
                            </Typography>
                          )}
                        </Box>
                        {emp.conversation?.unreadCount > 0 && (
                          <Box
                            sx={{
                              minWidth: 20,
                              height: 20,
                              px: 0.5,
                              borderRadius: 10,
                              bgcolor: 'primary.main',
                              color: 'primary.contrastText',
                              fontSize: 12,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {emp.conversation.unreadCount}
                          </Box>
                        )}
                        {startingId === emp.id && <CircularProgress size={16} />}
                      </ListItemButton>
                    );
                  })}
                </ul>
              </li>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
}

EmployeeDirectory.propTypes = {
  getDirectory: PropTypes.func.isRequired,
  onlineUsers: PropTypes.instanceOf(Set),
  onStartChat: PropTypes.func.isRequired,
  startingId: PropTypes.string,
};
